import {evite} from 'evite';
import urls from 'urls';
import {action, observable, computed} from 'mobx';
import {PaginationWithFilters} from '~/common/pagination/paginationWithFilters';
import {GuestContactModel} from './guest_contact_model';
import {FREE_GUEST_LIMIT} from '~/create/constants';
import {guestTitle} from '~/create/utils/formatters';
import {failedFetchModal} from '~/modal/_ajax_error_handler';

const GUESTS_PER_PAGE = 25;
const userHasSubscription = window.paymentCalcSummary
  ? window.paymentCalcSummary.userHasActivePremiumPass
  : false;

export class AddGuestStore {
  constructor(event) {
    this.event = event;
    this.contactStores = [];
    this.guests = new PaginationWithFilters(GUESTS_PER_PAGE, this.fetchGuests);
    // default sort
    this.guests.sortBy = 'name';
    this.applySearch = evite.debounce(this.applySearch, 500);
    this.setFilters();

    this.alreadySentInviteesCount = 0;
    if (window.guest_list_summary) this.alreadySentInviteesCount = window.guest_list_summary.count;
  }

  @observable accessor filters = {query: ''};

  @observable accessor editing = false;

  @observable accessor editedGuest = {};

  @observable accessor loaded = false;

  currentEditingKey = '';

  shouldTriggerUnlimitedGuestsModal = false;

  totalGuestsChanges = {oldValue: 0, newValue: 0};

  // state for invalid guests
  @observable accessor invalidGuests = [];

  @observable accessor shownInvalidGuests = [];

  isInvalidEditing = false;

  alreadySentInviteesCount = 0;

  registerContactStore(contactStore) {
    contactStore.setGuestStore(this);
    this.contactStores.push(contactStore);
  }

  /** *******************
   *Validation
   ******************** */
  validate() {
    if (!this.hasInvalids(true)) return '';

    const count = this.countInvalid(true);
    const title = guestTitle().toLowerCase();
    if (count === 1) return `Please fix or remove the invalid ${title}.`;
    return `Please fix or remove the ${count} invalid ${title}s.`;
  }

  /** *******************
   *Refreshing
   ******************** */

  // re-fetch the current page, likely due to changes made elsewhere
  refresh() {
    // noinspection JSIgnoredPromiseFromCall
    this.guests.fetchCurrentPage();
  }

  // tell our registered contact stores to refresh
  refreshContacts() {
    this.contactStores.forEach((contactStore) => contactStore.refresh());
  }

  // calls clearSearch() on each contact store (which refreshes too)
  clearContactsSearch() {
    this.contactStores.forEach((contactStore) => contactStore.clearSearch());
  }

  /** *******************
   *Filters
   ******************** */

  setFilters() {
    this.guests.setFilter(this.filters);
  }

  /** *******************
   * *Search
   * ******************** */

  @action
  applySearch() {
    this.setFilters();
    this.refresh();
  }

  @action
  clearSearch = () => {
    this.filters.query = '';
    this.setFilters();
    this.refresh();
  };

  @action
  search = (query) => {
    this.filters.query = query;
    this.applySearch();
  };

  /** *******************
   * *Invalid guests
   * ******************** */
  @action
  addInvalids = (guests) => {
    // dedupe the incoming guests w/ existing guests before adding
    const inModel = [];
    guests.forEach((guestObj) => {
      if (!this._hasInvalidGuestFor(guestObj)) {
        inModel.push(new GuestContactModel(guestObj, false, true, false));
      }
    });
    this.invalidGuests.push(...inModel);
    this.updateShownInvalids();
  };

  @action
  addAlreadySent = (guests) => {
    const inModel = guests.map(
      // kind of goofy, but the isGuest flag is
      // controlled by all others being false
      (guest) => new GuestContactModel(guest, false, false, false)
    );
    this.invalidGuests.push(...inModel);
    this.updateShownInvalids();
  };

  @action
  removeInvalid = (key) => {
    const index = this.invalidGuests.map((x) => x.key).indexOf(key);
    this.invalidGuests.splice(index, 1);
    this.updateShownInvalids();
  };

  @action
  removeAllInvalids = () => {
    this.invalidGuests = [];
    this.updateShownInvalids();
  };

  updateShownInvalids = () => {
    // whenever this.invalidGuests gets modified, this should
    // probably be called
    // TODO way to have this be called automatically any time this.invalidGuests changes?
    if (!this.hasInvalids()) {
      this.clearShownInvalids();
      return;
    }
    const queryString = this.filters.query ? this.filters.query.toLowerCase() : '';
    const matchingInvalids = this._getInvalidsMatching(queryString);
    if (matchingInvalids.length === 0) {
      this.clearShownInvalids();
      return;
    }

    const sortByField = this.guests.sortBy; // will be either "name" or "email"
    matchingInvalids.sort((a, b) =>
      a
        .comparisonValueFor(sortByField)
        .toString()
        .localeCompare(b.comparisonValueFor(sortByField).toString())
    );
    const {sortDirection} = this.guests; // will be either "ASC" or "DESC"
    if (sortDirection === 'DESC') {
      matchingInvalids.reverse(); // 'cause will be ascending after the sort(), above
    }

    this.setShownInvalids(matchingInvalids);
  };

  _getInvalidsMatching = (queryString) => {
    // queryString should be lowercase, BTW
    const hasQueryString = Boolean(queryString);
    const matchingInvalids = [];
    const numInvalids = this.countInvalid();
    for (let idx = 0; idx < numInvalids; idx++) {
      const invalidObj = this.invalidGuests[idx];
      if (!hasQueryString || invalidObj.matchesText(queryString)) matchingInvalids.push(invalidObj);
    }
    return matchingInvalids;
  };

  @action
  clearShownInvalids = () => {
    this.setShownInvalids([]);
  };

  @action
  setShownInvalids = (theseInvalids) => {
    this.shownInvalidGuests = theseInvalids;
  };

  countInvalid = (excludeDuplicates = false) => {
    if (excludeDuplicates) {
      return this.invalidGuests.filter((guest) => !guest.isGuest).length;
    }
    return this.invalidGuests.length;
  };

  hasInvalids = (excludeDuplicates = false) => this.countInvalid(excludeDuplicates) > 0;

  getInvalidsToBeShown = () => this.shownInvalidGuests;

  countShownInvalids = () => this.shownInvalidGuests.length;

  hasShownInvalids = () => this.countShownInvalids() > 0;

  _hasInvalidGuestFor = (guestObj) => {
    // incoming guestObj should have email, phone and name attributes
    const numInvalids = this.countInvalid();
    for (let idx = 0; idx < numInvalids; idx++) {
      const thisGuest = this.invalidGuests[idx];
      if (
        thisGuest.email == guestObj.email &&
        thisGuest.phone == guestObj.phone &&
        thisGuest.name == guestObj.name
      )
        return true;
    }
    return false;
  };

  setTotalGuestsChanges(currentOldValue, currentNewValue) {
    this.totalGuestsChanges.oldValue = currentOldValue;
    this.totalGuestsChanges.newValue = currentNewValue;
  }

  totalGuestsHasChanged(currentOldValue, currentNewValue) {
    return !(
      currentOldValue === this.totalGuestsChanges.oldValue &&
      currentNewValue === this.totalGuestsChanges.newValue
    );
  }

  /** *******************
   *fetching
   ******************** */

  fetchGuests = async ({start, limit, sortBy, sortDirection, ...filters}) => {
    // const invalidPages = Math.floor(this.invalidGuests.length / limit);
    if (this.event.id) {
      const url = urls.get('ajax_event_pagination', {
        event_id: this.event.id,
        query: filters.query,
        page: this.guests.currentPage + 1,
        // page: this.guests.currentPage + 1 - invalidPages,
        type: this.getDraftGuestListType(),
        searchBy: 'all',
        sortBy,
        reverse: sortDirection !== 'ASC',
        limit,
      });
      try {
        const response = await evite.fetch(url, {
          method: 'get',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        });
        if (response.ok) {
          const jsonResponse = await response.json();
          const {guests} = jsonResponse;
          this.guests.setTotals(guests.count);
          this.loaded = true;
          // this.guests.setTotals(guests.count + this.invalidGuests.length);
          // TODO load invalids if there are any then:
          this.updateShownInvalids();
          return guests.page.map((contact) => new GuestContactModel(contact, false, true, false));

          // return [...this.invalidGuests, ...fromServer];
        }
      } catch (e) {
        evite.error('Error in fetching guest');
        evite.error(e);
        return [];
      }
    }
    return [];
  };

  /** *******************
   *POSTING
   ******************** */

  /** *******************
   *add an array of  contacts
   ******************** */
  @action
  addGuests = async (guests) => {
    const url = urls.get('ajax_event_add_guest', {
      event_id: this.event.id,
      type: this.getDraftGuestListType(),
    });
    try {
      const response = await evite.fetch(url, {
        method: 'post',
        credentials: 'include',
        body: JSON.stringify(guests),
        headers: {
          Accept: 'application/json',
        },
      });
      if (response.ok) {
        const jsonResponse = await response.json();
        const {already_sent, guests} = jsonResponse;

        if (already_sent && already_sent.length) {
          this.addAlreadySent(already_sent);
        }

        return guests;
      }
    } catch (e) {
      evite.error('Error in posting guest');
      evite.error(e);
      return [];
    }
  };

  @action
  addGuestsFromAllContactsInGroup = async (contactGroupId, searchParams) => {
    const url = urls.get('ajax_event_add_guests_from_contacts', {
      event_id: this.event.id,
      groupId: contactGroupId,
      searchType: searchParams.searchType,
      searchBy: searchParams.searchBy,
      query: searchParams.query,
      sourceType: searchParams.type,
      targetType: this.getDraftGuestListType(),
    });
    try {
      const response = await evite.fetch(url, {
        method: 'post',
        credentials: 'include',
        body: '{}', // weird, but okay
        headers: {
          Accept: 'application/json',
        },
      });
      if (response.ok) {
        const jsonResponse = await response.json();
        const guestsAdded = jsonResponse.guests_added;
        return guestsAdded;
      }
      failedFetchModal(response);
      return [];
    } catch (e) {
      evite.error('Error in adding guests from contacts');
      evite.error(e);
      return [];
    }
  };

  /** *******************
   *Deleting
   ******************** */

  /** *******************
   *delete an array of  contacts
   ******************** */
  @action
  deleteGuest = async (guest_id) => {
    // TODO SEND guest_id to the delete url
    const url = urls.get('ajax_event_delete_guest', {
      event_id: this.event.id,
      type: this.getDraftGuestListType(),
      guest_id,
    });
    try {
      const response = await evite.fetch(url, {
        method: 'delete',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });
      if (response.ok) {
        const jsonResponse = await response.json();
        const {guests} = jsonResponse;
        return guests;
      }
    } catch (e) {
      evite.error('Error in posting guest');
      evite.error(e);
      return [];
    }
  };

  @action
  deleteGuestsFromContactGroup = async (contactGroupId, searchParams) => {
    const url = urls.get('ajax_event_delete_guests_from_contacts', {
      event_id: this.event.id,
      groupId: contactGroupId,
      searchType: searchParams.searchType,
      searchBy: searchParams.searchBy,
      query: searchParams.query,
      sourceType: searchParams.type,
      targetType: this.getDraftGuestListType(),
    });
    try {
      const response = await evite.fetch(url, {
        method: 'post',
        credentials: 'include',
        body: '{}', // weird, but okay
        headers: {
          Accept: 'application/json',
        },
      });
      if (response.ok) {
        const jsonResponse = await response.json();
        const idsOfGuestsRemoved = jsonResponse.ids_of_guests_removed;
        return idsOfGuestsRemoved;
      }
    } catch (e) {
      evite.error('Error in deleting guests from contacts');
      evite.error(e);
      return [];
    }
  };

  /** *******************
   *Editing
   ******************** */
  @action
  editGuestByKey = (guestKey, isValid = true, guest_id = null) => {
    this.currentEditingKey = guestKey;
    if (isValid) {
      this.editedGuest = this.guests.results.find(
        (cg) => cg.key === guestKey || cg.guest_id === guest_id
      );
    } else {
      this.isInvalidEditing = true;
      this.editedGuest = this.invalidGuests.find((cg) => cg.key === guestKey);
    }
    // used for editing contacts (non guests) on mobile
    if (!this.editedGuest && this.contactStores.length) {
      this.editedGuest = this.contactStores[0].contacts.results.find((cg) => cg.key === guestKey);
    }
    this.editedGuest.editGuest();
    this.editing = true;
  };

  @action
  cancelEditByKey = () => {
    this.editedGuest.cancelEdit();
    this.editing = false;
  };

  @action
  doneEdit = () => {
    this.editing = false;
    this.editedGuest = {};
    // remove invalidGuests with match key
    if (this.isInvalidEditing) {
      this.invalidGuests = this.invalidGuests.filter(
        (guest) => guest.key !== this.currentEditingKey
      );
      this.currentEditingKey = '';
      this.isInvalidEditing = false;
      this.updateShownInvalids();
    }
  };

  @action
  saveNewContactToGuest = async (contact) => {
    const url = urls.get('ajax_profile_contacts');
    try {
      const response = await evite.fetch(url, {
        method: 'PUT',
        credentials: 'include',
        body: JSON.stringify(contact),
        headers: {
          Accept: 'application/json',
        },
      });
      if (response.ok) {
        const jsonResponse = await response.json();
        const contact_id = jsonResponse.id;
        return contact_id;
      }
    } catch (e) {
      evite.error('Error in creating contact');
      evite.error(e);
    }
  };

  @action
  saveEditingGuest = async () => {
    if (this.editedGuest.guest_id) {
      const url = urls.get('ajax_event_update_guest', {
        event_id: this.event.id,
        type: this.getDraftGuestListType(),
      });

      try {
        await evite.fetch(url, {
          method: 'PATCH',
          credentials: 'include',
          body: JSON.stringify(this.editedGuest.editState),
          headers: {
            Accept: 'application/json',
          },
        });
      } catch (e) {
        evite.error('Error in updating guest');
        evite.error(e);
      }
    } else {
      // saving a new guest (invalid )
      await this.addGuests([this.editedGuest.editState]);
      // then we need to refresh table of valid ones
      await this.refresh();
    }
  };

  getDraftGuestListType = () => (this.event.is_host_viewing ? 'draft' : 'guest_invite');

  /** *******************
   *Computed values
   ******************** */
  get requiresUpgrade() {
    const isHostViewing = this.event.is_host_viewing;
    const {totalRecords} = this.guests;
    return (
      isHostViewing &&
      !userHasSubscription &&
      totalRecords + this.alreadySentInviteesCount > FREE_GUEST_LIMIT
    );
  }

  @computed
  get numDraftGuestsAdded() {
    return this.guests.totalRecords + this.invalidGuests.length || 0;
  }

  @computed
  get isInitialized() {
    return this.loaded;
  }
}
